/**
 * API Configuration Utility
 * Centralizes environment detection and API URL generation
 */

const LOCAL_DEFAULT_ORIGIN = 'http://localhost:8082'

const sanitizeBase = (value?: string) => {
  if (!value) return ''
  return value.trim().replace(/\/$/, '')
}

const isBrowser = () => typeof window !== 'undefined'

const isLocalhost = (hostname: string) => hostname === 'localhost' || hostname === '127.0.0.1'

export const getFastApiBaseOrigin = (): string => {
  const envBase = sanitizeBase(import.meta.env?.VITE_FASTAPI_URL || import.meta.env?.VITE_API_URL)
  if (envBase) {
    return envBase
  }

  if (!isBrowser()) {
    return LOCAL_DEFAULT_ORIGIN
  }

  const { protocol, hostname, origin } = window.location

  if (isLocalhost(hostname)) {
    // Same-origin via Vite /api proxy — avoids CORS to :8082 in local dev.
    return ''
  }

  // Qwixl:Academy custom domains
  if (hostname === 'academy.qwixl.com' || hostname === 'www.academy.qwixl.com') {
    return 'https://api.academy.qwixl.com'
  }
  if (hostname === 'stg.academy.qwixl.com') {
    return 'https://stg.api.academy.qwixl.com'
  }

  // Legacy Omnis custom domain
  if (hostname === 'omnis.education' || hostname === 'www.omnis.education') {
    return 'https://omnis-api-prd.vercel.app'
  }

  // For deployed Vercel environments, infer backend URL from frontend URL
  // Frontend: omnis-stg.vercel.app -> Backend: omnis-api-stg.vercel.app
  // Frontend: omnis-prd.vercel.app -> Backend: omnis-api-prd.vercel.app
  if (hostname.includes('vercel.app')) {
    if (hostname.includes('omnis-stg')) {
      return 'https://omnis-api-stg.vercel.app'
    }
    if (hostname.includes('omnis-prd') || hostname.includes('omnis')) {
      return 'https://omnis-api-prd.vercel.app'
    }
    // Generic fallback: try to construct backend URL
    const backendHostname = hostname.replace(/^([^.]+)/, '$1-api')
    return `${protocol}//${backendHostname}`
  }

  // Fallback to same origin
  return origin
}

export const buildFastApiUrl = (path: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const base = getFastApiBaseOrigin()

  if (!base) {
    return normalizedPath
  }

  return `${base}${normalizedPath}`
}

/** Build a fetch-ready FastAPI URL with query params (works with localhost Vite /api proxy). */
export const buildFastApiRequestUrl = (
  path: string,
  params?: Record<string, string | number | undefined | null>,
): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const search = new URLSearchParams()

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        search.set(key, String(value))
      }
    }
  }

  const qs = search.toString()
  const base = getFastApiBaseOrigin()

  if (!base) {
    return qs ? `${normalizedPath}?${qs}` : normalizedPath
  }

  const url = new URL(`${base}${normalizedPath}`)
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        url.searchParams.set(key, String(value))
      }
    }
  }
  return url.toString()
}

export const API_BASE = getFastApiBaseOrigin()

export const apiPath = (path: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  if (!API_BASE) {
    return normalizedPath
  }
  return `${API_BASE}${normalizedPath}`
}

export const getApiUrl = () => buildFastApiUrl('/api')

export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  return fetch(apiPath(path), init)
}

export async function authedApiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const { getAuthenticatedHeadersObject } = await import('./apiHeaders')
  const authHeaders = await getAuthenticatedHeadersObject()
  const providedHeaders = new Headers(init.headers)
  const headers: Record<string, string> = { ...authHeaders }

  providedHeaders.forEach((value, key) => {
    headers[key] = value
  })

  return fetch(apiPath(path), {
    ...init,
    headers,
  })
}

export const getCurrentEnvironment = () => {
  if (!isBrowser()) {
    return 'local'
  }

  const hostname = window.location.hostname

  if (isLocalhost(hostname)) {
    return 'local'
  }
  if (hostname.includes('dev-omnis') || hostname.includes('omnis-dev')) {
    return 'dev'
  }
  if (hostname.includes('stg-omnis') || hostname.includes('omnis-stg')) {
    return 'staging'
  }
  return 'production'
}

export const isLocalDevelopment = () => getCurrentEnvironment() === 'local'
export const isProduction = () => getCurrentEnvironment() === 'production'

/** Local Vite dev pointing at a remote API (e.g. stg.api) — browser blocks cross-origin fetches. */
export const isRemoteApiFromLocalhost = (): boolean => {
  if (!isBrowser()) return false
  const hostname = window.location.hostname
  if (!isLocalhost(hostname)) return false
  if (!API_BASE) return false
  try {
    const apiHost = new URL(API_BASE).hostname
    return !isLocalhost(apiHost)
  } catch {
    return false
  }
}

const academyHostnames = new Set([
  'academy.qwixl.com',
  'www.academy.qwixl.com',
  'stg.academy.qwixl.com',
])

/** True on academy.qwixl.com product domains (login-only; marketing lives on qwixl.com). */
export const isAcademyProductDomain = (): boolean => {
  if (import.meta.env.VITE_ACADEMY_PRODUCT_MODE === 'true') {
    return true
  }
  if (!isBrowser()) {
    return false
  }
  return academyHostnames.has(window.location.hostname)
}

export const getFirebaseProjectId = () => {
  switch (getCurrentEnvironment()) {
    case 'local':
    case 'dev':
      return 'dev-omnis'
    case 'staging':
      return 'stg-omnis'
    case 'production':
    default:
      return 'prd-omnis'
  }
}
